const PARAMS_KEY = 'companion_auth_params';

// The original /external-auth query (sessionId, platform, meetingUrl, and the
// platform-specific webex_*/teams_* params) is stashed before the Auth0 SPA
// login — the login round-trip drops the query (the guard's returnTo is
// pathname-only) — and read back afterwards. The completion page reads it to
// resolve the meeting URL and open the deeplink. sessionStorage is per-origin
// and survives the redirects to Auth0 / the backend and back in the same tab.
export function stashCompanionAuthParams(query: string): void {
  try {
    sessionStorage.setItem(PARAMS_KEY, query);
  } catch {
    // sessionStorage unavailable (private mode) — non-fatal.
  }
}

function read(): URLSearchParams | null {
  try {
    const raw = sessionStorage.getItem(PARAMS_KEY);
    return raw ? new URLSearchParams(raw) : null;
  } catch {
    return null;
  }
}

// Launcher reads without clearing (the completion page still needs the params).
export function peekCompanionAuthParams(): URLSearchParams | null {
  return read();
}

// Completion reads then clears.
export function readAndClearCompanionAuthParams(): URLSearchParams | null {
  const params = read();
  try {
    sessionStorage.removeItem(PARAMS_KEY);
  } catch {
    // ignore
  }
  return params;
}

const FORCE_LOGIN_KEY = 'companion_force_login';

// Set by the completion page when a subject mismatch is detected. The launcher
// reads and clears it on the next run to request prompt=login from the backend.
export function setCompanionForceLogin(): void {
  try {
    sessionStorage.setItem(FORCE_LOGIN_KEY, '1');
  } catch {
    // sessionStorage unavailable (private mode) — non-fatal.
  }
}

// Reads and clears the force-login flag in one operation. Returns true if the
// flag was set (caller should use promptLogin=true for the next authorize call).
export function readAndClearCompanionForceLogin(): boolean {
  try {
    const val = sessionStorage.getItem(FORCE_LOGIN_KEY);
    if (val) {
      try {
        sessionStorage.removeItem(FORCE_LOGIN_KEY);
      } catch {
        // ignore
      }
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

const RETRY_ATTEMPTED_KEY = 'companion_retry_attempted';

// One-shot cap on the auto-retry. Set by the completion page before it navigates
// back to the launcher; a second consecutive retryable error in the same flow then
// finds it set and shows an error instead of looping.
export function setCompanionRetryAttempted(): void {
  try {
    sessionStorage.setItem(RETRY_ATTEMPTED_KEY, '1');
  } catch {
    // sessionStorage unavailable (private mode) — non-fatal.
  }
}

// Pure read (does not clear) — the completion page reads it at render to decide
// whether a retry is still allowed for this flow.
export function peekCompanionRetryAttempted(): boolean {
  try {
    return sessionStorage.getItem(RETRY_ATTEMPTED_KEY) === '1';
  } catch {
    return false;
  }
}

// Cleared on a fresh launch (new flow, by the launcher) and on success.
export function clearCompanionRetryAttempted(): void {
  try {
    sessionStorage.removeItem(RETRY_ATTEMPTED_KEY);
  } catch {
    // ignore
  }
}
